/**
 * DatabaseBanner - Database connection controls
 *
 * Shows current database status and provides open/create/save actions.
 * Displays file name and student count when connected.
 * Shows lock icon for encrypted databases.
 */

import { useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Database,
  FolderOpen,
  FilePlus2,
  Save,
  HardDrive,
  AlertCircle,
  Lock,
  LockOpen,
  LockKeyhole,
  ShieldPlus,
} from "lucide-react";
import { useLocalDbStore, useDatabaseState, useSupportsFileSystemAccess } from "@/store/localDbStore";
import { PasswordDialog } from "./PasswordDialog";

export function DatabaseBanner() {
  const {
    isConnected,
    isDirty,
    loading,
    error,
    openDatabase,
    createDatabase,
    createEncryptedDatabase,
    enableEncryption,
    lockDatabase,
    saveDatabase,
    clearError,
  } = useLocalDbStore();

  const { fileName, classCount, studentCount, isEncrypted } = useDatabaseState();
  const supportsFileSystem = useSupportsFileSystemAccess();

  // Create database dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [encryptNewDb, setEncryptNewDb] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showEnableEncryptionDialog, setShowEnableEncryptionDialog] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [enableEncryptionError, setEnableEncryptionError] = useState<string | null>(null);

  const handleCreateClick = useCallback(() => {
    setShowCreateDialog(true);
    setEncryptNewDb(false);
  }, []);

  const handleCreateConfirm = useCallback(() => {
    setShowCreateDialog(false);
    if (encryptNewDb) {
      setShowPasswordDialog(true);
    } else {
      createDatabase();
    }
  }, [encryptNewDb, createDatabase]);

  const handlePasswordSubmit = useCallback(
    async (password: string) => {
      setCreateError(null);
      try {
        await createEncryptedDatabase(password);
        setShowPasswordDialog(false);
      } catch (err) {
        setCreateError(
          err instanceof Error ? err.message : "Failed to create encrypted database"
        );
      }
    },
    [createEncryptedDatabase]
  );

  const handlePasswordDialogClose = useCallback((open: boolean) => {
    if (!open) {
      setShowPasswordDialog(false);
      setCreateError(null);
    }
  }, []);

  const handleEnableEncryptionClick = useCallback(() => {
    setShowEnableEncryptionDialog(true);
    setEnableEncryptionError(null);
  }, []);

  const handleEnableEncryptionSubmit = useCallback(
    async (password: string) => {
      setEnableEncryptionError(null);
      try {
        await enableEncryption(password);
        setShowEnableEncryptionDialog(false);
        toast.success("Database encrypted successfully");
      } catch (err) {
        setEnableEncryptionError(
          err instanceof Error ? err.message : "Failed to encrypt database"
        );
      }
    },
    [enableEncryption]
  );

  const handleEnableEncryptionDialogClose = useCallback((open: boolean) => {
    if (!open) {
      setShowEnableEncryptionDialog(false);
      setEnableEncryptionError(null);
    }
  }, []);

  // Error state
  if (error) {
    return (
      <Alert variant="destructive" className="mb-6">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription className="flex items-center justify-between">
          <span>{error}</span>
          <Button variant="ghost" size="sm" onClick={clearError}>
            Dismiss
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // Not connected
  if (!isConnected) {
    return (
      <>
        <Alert className="mb-6 bg-muted/50">
          <Database className="h-4 w-4" />
          <AlertDescription className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="font-medium">No database connected</p>
              <p className="text-sm text-muted-foreground">
                Open an existing database or create a new one to manage student names.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={openDatabase}
                disabled={loading}
              >
                <FolderOpen className="mr-2 h-4 w-4" />
                Open
              </Button>
              <Button
                size="sm"
                onClick={handleCreateClick}
                disabled={loading}
              >
                <FilePlus2 className="mr-2 h-4 w-4" />
                Create New
              </Button>
            </div>
          </AlertDescription>
        </Alert>

        {/* Create Database Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Create New Database</DialogTitle>
              <DialogDescription>
                Create a new database to store your student names locally.
              </DialogDescription>
            </DialogHeader>

            <div className="flex items-center space-x-2 py-4">
              <Checkbox
                id="encrypt"
                checked={encryptNewDb}
                onCheckedChange={(checked) => setEncryptNewDb(checked === true)}
              />
              <Label
                htmlFor="encrypt"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Encrypt with password
              </Label>
            </div>

            {encryptNewDb && (
              <p className="text-sm text-muted-foreground">
                You will be prompted to set a password. The database will be
                encrypted using AES-256 and can only be opened with the
                password.
              </p>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleCreateConfirm}>
                {encryptNewDb ? "Set Password" : "Create Database"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Password Dialog for Encrypted Database Creation */}
        <PasswordDialog
          open={showPasswordDialog}
          onOpenChange={handlePasswordDialogClose}
          mode="create"
          onSubmit={handlePasswordSubmit}
          error={createError}
          loading={loading}
        />
      </>
    );
  }

  // Connected
  return (
    <>
      <Alert className="mb-6 bg-primary/5 border-primary/20">
        <HardDrive className="h-4 w-4 text-primary" />
        <AlertDescription className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div>
              <p className="font-medium text-primary flex items-center gap-2">
                {isEncrypted ? (
                  <Lock className="h-4 w-4 text-green-600" aria-label="Encrypted" />
                ) : (
                  <LockOpen className="h-4 w-4 text-muted-foreground" aria-label="Not encrypted" />
                )}
                {fileName}
              </p>
              <p className="text-sm text-muted-foreground">
                {classCount} class{classCount !== 1 ? "es" : ""} &middot;{" "}
                {studentCount} student{studentCount !== 1 ? "s" : ""}
                {isEncrypted && (
                  <span className="text-green-600"> &middot; Encrypted</span>
                )}
                {isDirty && !supportsFileSystem && (
                  <span className="text-amber-600"> &middot; Unsaved changes</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {isEncrypted && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={lockDatabase}
                    disabled={loading}
                  >
                    <LockKeyhole className="mr-2 h-4 w-4" />
                    Lock
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Lock database and clear password from memory</p>
                </TooltipContent>
              </Tooltip>
            )}
            {!isEncrypted && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleEnableEncryptionClick}
                    disabled={loading}
                  >
                    <ShieldPlus className="mr-2 h-4 w-4" />
                    Add Password
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Encrypt this database with a password</p>
                </TooltipContent>
              </Tooltip>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={openDatabase}
              disabled={loading}
            >
              <FolderOpen className="mr-2 h-4 w-4" />
              Open
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={saveDatabase}
              disabled={loading || (!isDirty && supportsFileSystem)}
            >
              <Save className="mr-2 h-4 w-4" />
              {supportsFileSystem ? "Save" : "Download"}
            </Button>
          </div>
        </AlertDescription>
      </Alert>

      {/* Password Dialog for Enabling Encryption on Existing Database */}
      <PasswordDialog
        open={showEnableEncryptionDialog}
        onOpenChange={handleEnableEncryptionDialogClose}
        mode="create"
        onSubmit={handleEnableEncryptionSubmit}
        error={enableEncryptionError}
        loading={loading}
      />
    </>
  );
}
